import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mic, Send, User, Bot, StopCircle, Sparkles, Building2 } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../services/AuthContext';


interface AIChatOverlayProps {
    isOpen: boolean;
    onClose: () => void;
}

interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    relatedProperties?: any[];
    generated_image_url?: string;
}

const AIChatOverlay: React.FC<AIChatOverlayProps> = ({ isOpen, onClose }) => {
    const { user } = useAuth();
    const [contextProperty, setContextProperty] = useState<any>(null);
    const [messages, setMessages] = useState<Message[]>([
        { 
            id: '1', 
            role: 'assistant', 
            content: "Hello! I'm your AI Property Architect. Analyze market trends, compare properties, or visualize renovations instantly.", 
            timestamp: new Date() 
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isListening, setIsListening] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    const handleSendMessage = async () => {
        if (!inputValue.trim()) return;

        const newUserMsg: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: inputValue,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, newUserMsg]);
        setInputValue('');
        setIsTyping(true);

        try {
            let activeSessionId = currentSessionId;
            if (!activeSessionId && user?.id) {
                try {
                    const sessRes = await api.post('/api/v1/history/session', { 
                        title: inputValue.length > 30 ? inputValue.substring(0, 30) + '...' : inputValue,
                        user_id: user.id 
                    });
                    activeSessionId = sessRes.data.session_id;
                    setCurrentSessionId(activeSessionId);
                } catch(e) { console.error("Session creation failed", e); }
            }

            const response = await api.post('/api/v1/live-ai/chat', { 
                message: newUserMsg.content,
                context_listings: contextProperty ? [contextProperty.id] : [],
                session_id: activeSessionId
            });

            if (!activeSessionId && response.data.session_id) {
                setCurrentSessionId(response.data.session_id);
            }

            const aiMsg: Message = {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: response.data.response || "I encountered an issue processing that request. Please try again.",
                timestamp: new Date(),
                relatedProperties: response.data.listings || [],
                generated_image_url: response.data.generated_image_url
            };
            setMessages(prev => [...prev, aiMsg]);

        } catch (error) {
            console.error('AI Chat Error:', error);
            const errorMsg: Message = {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: "I'm having trouble connecting to the server. Please try again later.",
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMsg]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const toggleListening = () => {
        setIsListening(!isListening);
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-slate-900/10 backdrop-blur-[1px] z-[200]"
                    />

                    {/* Chat Panel - SLIMMER & MINIMAL */}
                    <motion.div
                        initial={{ x: '100%', opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: '100%', opacity: 0 }}
                        transition={{ type: 'spring', damping: 30, stiffness: 250 }}
                        className="fixed top-0 end-0 h-full w-full sm:w-[420px] bg-white/95 backdrop-blur-xl z-[210] border-s border-zinc-100 flex flex-col overflow-hidden shadow-2xl"
                    >
                        {/* Header Area */}
                        <div className="flex items-center justify-between px-6 py-6 border-b border-zinc-100">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-md bg-[#1E4164] flex items-center justify-center flex-shrink-0 shadow-sm">
                                    <Sparkles className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-base font-bold text-zinc-900 tracking-tight">AI Architect</h2>
                                        <div className="w-1.5 h-1.5 rounded-full bg-[#1E4164] shadow-[0_0_8px_rgba(10,68,104,0.5)] animate-pulse" />
                                    </div>
                                    <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-[0.15em] mt-0.5">Property Intelligence</p>
                                </div>
                            </div>
                            <button 
                                onClick={onClose}
                                className="p-2 rounded-md hover:bg-zinc-100 text-zinc-400 transition-colors active:scale-95"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Smart Context Bar */}
                        <AnimatePresence>
                            {contextProperty && (
                                <motion.div 
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="px-6 py-3 bg-zinc-50 border-b border-zinc-100"
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0 bg-zinc-200">
                                                <img src={contextProperty.image} className="w-full h-full object-cover" alt="" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider">Active Context</p>
                                                <p className="text-xs font-bold text-zinc-700 truncate">{contextProperty.title}</p>
                                            </div>
                                        </div>
                                        <button 
                                            onClick={() => setContextProperty(null)}
                                            className="p-1 hover:bg-zinc-200 rounded text-zinc-400"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Messages Area */}
                        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 scrollbar-hide">
                            {messages.map((msg) => (
                                <motion.div 
                                    key={msg.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`flex items-start gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                                >
                                    {/* Avatar */}
                                    <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 shadow-sm border ${
                                        msg.role === 'user' 
                                            ? 'bg-white border-zinc-100 text-zinc-600' 
                                            : 'bg-[#1E4164] border-[#1E4164] text-white'
                                    }`}>
                                        {msg.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                                    </div>

                                    {/* Content Wrapper */}
                                    <div className={`flex flex-col gap-2 max-w-[85%] ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                                        <div className={`px-5 py-3.5 text-[13px] leading-relaxed font-medium shadow-sm border ${
                                            msg.role === 'user'
                                                ? 'bg-[#1E4164] text-white border-[#1E4164] rounded-md rounded-se-none'
                                                : 'bg-white text-zinc-600 border-zinc-100 rounded-md rounded-ss-none'
                                        }`}>
                                            <div className="whitespace-pre-wrap">{msg.content}</div>
                                        </div>

                                        {msg.generated_image_url && (
                                            <div className="mt-1 rounded-md overflow-hidden shadow-lg border border-zinc-100 group relative">
                                                <img 
                                                    src={msg.generated_image_url} 
                                                    alt="AI Generated" 
                                                    className="w-full h-auto max-h-[250px] object-cover"
                                                />
                                            </div>
                                        )}

                                        {/* Property Cards - More compact */}
                                        {msg.role === 'assistant' && msg.relatedProperties && msg.relatedProperties.length > 0 && (
                                            <div className="w-full flex flex-col gap-2 mt-1">
                                                {msg.relatedProperties.slice(0, 2).map((prop: any) => (
                                                    <div 
                                                        key={prop.id} 
                                                        className="bg-white p-3 rounded-md border border-zinc-200 shadow-sm flex gap-3 hover:border-[#1E4164] transition-all cursor-pointer group"
                                                    >
                                                        <div className="w-14 h-14 rounded-md bg-zinc-50 overflow-hidden flex-shrink-0">
                                                            {prop.image ? (
                                                                <img src={prop.image} alt={prop.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-zinc-200"><Building2 className="w-6 h-6" /></div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                                                            <h4 className="text-xs font-bold text-zinc-900 truncate leading-tight group-hover:text-[#1E4164] transition-colors">{prop.title}</h4>
                                                            <p className="text-[10px] text-zinc-500 font-bold truncate mt-0.5">{prop.location}</p>
                                                            <p className="text-xs font-bold text-zinc-900 mt-1">
                                                                {prop.price ? prop.price.toLocaleString() : 'P.O.R'} <span className="text-[9px] opacity-60 font-medium">EGP</span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            ))}

                            {isTyping && (
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-md bg-[#1E4164] border border-[#1E4164] text-white flex items-center justify-center">
                                        <Bot className="w-4 h-4 animate-pulse" />
                                    </div>
                                    <div className="bg-white/50 border border-zinc-100 rounded-md rounded-tl-none px-4 py-3 flex gap-1 items-center">
                                        <span className="w-1 h-1 bg-zinc-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                                        <span className="w-1 h-1 bg-zinc-300 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                                        <span className="w-1 h-1 bg-zinc-200 rounded-full animate-bounce"></span>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area - REFINED & COMPACT */}
                        <div className="px-6 py-6 bg-white border-t border-zinc-100">
                            <AnimatePresence>
                                {isListening && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="mb-4"
                                    >
                                        <div className="bg-[#1E4164] text-white rounded-md p-3 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                                                <span className="text-[11px] font-bold uppercase tracking-widest text-white/90">Listening...</span>
                                            </div>
                                            <button onClick={toggleListening} className="p-1 hover:bg-white/10 rounded">
                                                <StopCircle className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className="relative group">
                                <div className="flex items-center gap-2 p-1 bg-zinc-50 border border-zinc-200 rounded-md group-focus-within:border-[#1E4164]/30 group-focus-within:bg-white group-focus-within:ring-4 group-focus-within:ring-[#1E4164]/10 transition-all shadow-sm">
                                    <input 
                                        type="text"
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Type your message..." 
                                        className="flex-1 bg-transparent py-3 ps-4 text-xs font-medium text-zinc-900 focus:outline-none focus:ring-0 border-none placeholder:text-zinc-400"
                                        autoFocus
                                    />
                                    <div className="flex items-center gap-1 pe-1">
                                        <button 
                                            onClick={toggleListening}
                                            className={`p-2 rounded-md transition-colors ${
                                                isListening ? 'bg-red-50 text-red-500' : 'text-zinc-400 hover:text-[#1E4164]'
                                            }`}
                                        >
                                            <Mic className="w-4 h-4" />
                                        </button>
                                        <button 
                                            onClick={handleSendMessage}
                                            disabled={!inputValue.trim()}
                                            className={`p-2 rounded-md transition-all ${
                                                inputValue.trim()
                                                    ? 'bg-[#1E4164] text-white shadow-md'
                                                    : 'text-zinc-300 pointer-events-none'
                                            }`}
                                        >
                                            <Send className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            
                            <p className="mt-4 text-[9px] text-zinc-400 font-bold uppercase tracking-[0.2em] text-center opacity-60">
                                LAQY AI
                            </p>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default AIChatOverlay;

